import { formatJpWithAlias } from '@/utils/partDisplay';
import PartNoLabel from '@/components/PartNoLabel';
import StockBadge from '@/components/StockBadge';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Plus, Minus, Trash2, X, Save, ShoppingBag, Package, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditOrderModal = ({ isOpen, open, onClose, order, onOrderUpdated, onSave, isCustomer = false }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Support both prop patterns
  const isDialogOpen = isOpen || open;
  const handleOrderUpdated = onOrderUpdated || onSave;
  
  // Order items state
  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [customerDiscount, setCustomerDiscount] = useState(0);
  
  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productImages, setProductImages] = useState({});
  const [specialPrices, setSpecialPrices] = useState({}); // {jp_part_no: special_price}
  
  // Add item dialog
  const [addItemProduct, setAddItemProduct] = useState(null);
  const [addItemQuantity, setAddItemQuantity] = useState(0);
  const qtyInputRef = useRef(null);
  
  // Duplicate Item Confirmation Dialog
  const [duplicateItemConfirm, setDuplicateItemConfirm] = useState(null); // {product, existingQty, newQty}
  
  // Fetch special prices for this customer when order loads
  useEffect(() => {
    const fetchSpecialPrices = async () => {
      if (order?.customer_id && token) {
        try {
          const response = await axios.get(`${API}/price-requests/admin/special-prices`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Filter special prices for this customer
          const customerPrices = {};
          (response.data.special_prices || []).forEach(sp => {
            if (sp.customer_id === order.customer_id) {
              customerPrices[sp.jp_part_no] = sp.special_price;
            }
          });
          setSpecialPrices(customerPrices);
        } catch (error) {
          console.error('Error fetching special prices:', error);
        }
      }
    };
    fetchSpecialPrices();
  }, [order?.customer_id, token]);
  
  // Initialize items when order changes
  useEffect(() => {
    if (!order || !order.items) return;
    let cancelled = false;
    const orderItems = order.items.map(item => ({
      ...item,
      _original: true // Mark as original item
    }));
    setItems(orderItems);
    setOriginalItems(JSON.parse(JSON.stringify(orderItems)));
    setCustomerDiscount(order.discount_percentage || 0);

    // 2026-06-02 — Bulk-enrich each item with canonical / OE / legacy
    // fields so `formatJpWithAlias(item)` can render
    // `JPSEV1234 (OE: …)` or `(LEGACY: …)`. Without this enrichment,
    // the order line snapshot has only the legacy `jp_part_no` and
    // the helper falls back to bare display.
    (async () => {
      const codes = [...new Set(orderItems.map(i => i.jp_part_no).filter(Boolean))];
      if (codes.length === 0) return;
      try {
        const { data } = await axios.post(
          `${API}/parts/bulk-alias-lookup`,
          { codes },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        const lookup = data?.results || {};
        if (Object.keys(lookup).length === 0) return;
        setItems(prev => prev.map(it => {
          const p = lookup[it.jp_part_no];
          if (!p) return it;
          // Don't overwrite anything the order already carries —
          // we only ADD the alias fields the helper needs.
          return {
            ...it,
            new_jp_part_no: it.new_jp_part_no || p.new_jp_part_no || '',
            legacy_jp_part_no: it.legacy_jp_part_no || p.legacy_jp_part_no || '',
            old_jp_part_no: it.old_jp_part_no || p.old_jp_part_no || '',
            oe_part_no: it.oe_part_no || p.oe_part_no || '',
            // 2026-02-20 RC-151 — hydrate warehouse_moq so admin
            // qty controls snap correctly + badge renders.
            warehouse_moq: it.warehouse_moq || p.warehouse_moq || 1,
            customer_moq: it.customer_moq || p.customer_moq || 1,
            moq: it.moq || p.moq || 1,
          };
        }));
      } catch (e) {
        // Quiet — the helper will gracefully fall back to bare display.
      }
    })();

    return () => { cancelled = true; };
  }, [order, token]);
  
  // Debounce search
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };
  
  // Request ID ref for stale result prevention
  const productSearchIdRef = useRef(0);
  
  const debouncedProductSearch = useDebounce(productSearch, 250);
  
  // Search products and merge with special prices.
  // 2026-05: hardened with AbortController + literal-query check to
  // prevent stale results (slow "BU" beating fast "BUMP").
  useEffect(() => {
    const abortController = new AbortController();
    const thisQuery = debouncedProductSearch.trim();
    const doSearch = async () => {
      if (thisQuery.length >= 2 && token) {
        const thisRequestId = ++productSearchIdRef.current;
        setLoadingProducts(true);
        try {
          const response = await axios.get(
            `${API}/categories/All%20Products/parts?search=${encodeURIComponent(thisQuery)}&limit=20&include_inactive_brands=1`,
            { headers: { Authorization: `Bearer ${token}` }, signal: abortController.signal }
          );

          // Guard 1: latest fetch only.
          if (thisRequestId !== productSearchIdRef.current) {
            console.log(`[EditOrder Search] Discarding stale results for "${thisQuery}"`);
            return;
          }
          // Guard 2: query we kicked off still matches what's typed.
          if (thisQuery !== debouncedProductSearch.trim()) return;

          let parts = response.data?.parts || response.data || [];
          
          // Merge special prices into search results
          if (Array.isArray(parts)) {
            parts = parts.map(part => ({
              ...part,
              special_price: specialPrices[part.jp_part_no] || null,
              has_special_price: !!specialPrices[part.jp_part_no]
            }));
          }
          
          setSearchResults(Array.isArray(parts) ? parts : []);
          
          // Load images
          if (Array.isArray(parts)) {
            parts.forEach(part => {
              if (part.image && !productImages[part.jp_part_no]) {
                loadProductImage(part.jp_part_no, part.image);
              }
            });
          }
        } catch (error) {
          if (error.name === 'AbortError' || error.name === 'CanceledError') return;
          if (thisRequestId === productSearchIdRef.current
              && thisQuery === debouncedProductSearch.trim()) {
            console.error('Error searching products:', error);
            setSearchResults([]);
          }
        } finally {
          if (thisRequestId === productSearchIdRef.current) {
            setLoadingProducts(false);
          }
        }
      } else {
        setSearchResults([]);
      }
    };
    doSearch();
    return () => abortController.abort();
  }, [debouncedProductSearch, token, specialPrices]);
  
  // Load product image
  const loadProductImage = async (partNo, imageName) => {
    try {
      const response = await axios.get(`${API}/image/${imageName}`);
      if (response.data.data_url) {
        setProductImages(prev => ({ ...prev, [partNo]: response.data.data_url }));
      }
    } catch (error) {
      console.error('Error loading image:', error);
    }
  };
  
  // Open add item dialog
  const openAddItemDialog = (product) => {
    setAddItemProduct(product);
    // 2026-07-28 · MOQ-aware default. Pre-fill the qty at the part's
    // warehouse_moq (or 1 for MOQ-1 parts) so admins can add a
    // valid line in one click. Users complained about MB293356
    // (warehouse_moq=50) letting through sub-MOQ qty because the
    // dialog started at 0 with no MOQ hint.
    const _moq = parseInt(product?.warehouse_moq || product?.moq || 1, 10) || 1;
    setAddItemQuantity(_moq > 1 ? _moq : 1);
  };
  
  // Confirm add item - uses special price if available
  const confirmAddItem = () => {
    if (!addItemProduct || addItemQuantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    // 2026-07-28 · Snap-up to next warehouse_moq multiple before
    // adding so a manually-typed non-multiple (e.g. 10 for a MOQ-50
    // part) doesn't slip past the frontend and get server-side snapped
    // with no visible feedback.
    const _moq = parseInt(addItemProduct.warehouse_moq
                          || addItemProduct.moq || 1, 10) || 1;
    let _qty = parseInt(addItemQuantity, 10) || 0;
    if (_moq > 1 && _qty > 0 && _qty % _moq !== 0) {
      const _snapped = Math.ceil(_qty / _moq) * _moq;
      toast.info(
        `MOQ is ${_moq} PC. Rounded ${_qty} → ${_snapped} PC.`);
      _qty = _snapped;
    }
    
    const existingIndex = items.findIndex(item => item.jp_part_no === addItemProduct.jp_part_no);
    
    if (existingIndex >= 0) {
      // Show confirmation dialog for duplicate item
      setDuplicateItemConfirm({
        product: addItemProduct,
        existingQty: items[existingIndex].quantity,
        newQty: _qty
      });
      return;
    }
    
    // Add new item directly
    addNewItemToOrder(addItemProduct, _qty);
  };
  
  // Actual function to add item to order
  const addNewItemToOrder = (product, quantity, mergeWithExisting = false) => {
    const existingIndex = items.findIndex(item => item.jp_part_no === product.jp_part_no);
    
    if (existingIndex >= 0 && mergeWithExisting) {
      // Update existing item quantity
      const updatedItems = [...items];
      const oldQty = updatedItems[existingIndex].quantity;
      updatedItems[existingIndex].quantity = oldQty + quantity;
      setItems(updatedItems);
      toast.success(`Updated ${product.part_name}: ${oldQty} + ${quantity} = ${oldQty + quantity} PC`);
    } else if (existingIndex < 0) {
      // Add new item - CHECK FOR SPECIAL PRICE FIRST
      const unitPrice = product.mrp || 0;
      const hasSpecialPrice = product.has_special_price || !!specialPrices[product.jp_part_no];
      const specialPrice = product.special_price || specialPrices[product.jp_part_no];
      
      // Use special price if available, otherwise apply customer discount
      const discountedPrice = hasSpecialPrice 
        ? specialPrice 
        : unitPrice * (1 - customerDiscount / 100);
      
      const newItem = {
        jp_part_no: product.jp_part_no,
        part_name: product.part_name,
        oe_part_no: product.oe_part_no,
        // 2026-02-20 RC-151 — copy MOQ metadata onto the new line so
        // qty controls snap correctly and the badge renders.
        moq: product.moq || 1,
        customer_moq: product.customer_moq || 1,
        warehouse_moq: product.warehouse_moq || 1,
        mrp: unitPrice,
        price: discountedPrice,
        discounted_price: discountedPrice,
        quantity: quantity,
        subtotal: discountedPrice * quantity,
        has_special_price: hasSpecialPrice,
        _new: true // Mark as new item
      };
      
      setItems([...items, newItem]);
      toast.success(`Added ${product.part_name}${hasSpecialPrice ? ' (Special Price)' : ''}`);
    }
    
    // Reset state
    setAddItemProduct(null);
    setAddItemQuantity(0);
    setProductSearch('');
    setSearchResults([]);
    setDuplicateItemConfirm(null);
  };
  
  // Handle duplicate item confirmation
  const handleDuplicateConfirm = (merge) => {
    if (!duplicateItemConfirm) return;
    
    if (merge) {
      // Merge quantities
      addNewItemToOrder(duplicateItemConfirm.product, duplicateItemConfirm.newQty, true);
    } else {
      // Cancel - just close dialogs
      setDuplicateItemConfirm(null);
      setAddItemProduct(null);
      setAddItemQuantity(0);
    }
  };
  
  // Update item quantity
  const updateItemQuantity = (index, newQty) => {
    if (newQty < 1) return;
    const updatedItems = [...items];
    // 2026-02-20 RC-151 — Snap up to warehouse_moq multiple. The
    // qty coming in from +/- buttons is already `current +/- 1`;
    // if the part has a warehouse_moq > 1, step by that amount
    // instead and re-align any legacy non-multiple value.
    const moq = Math.max(1, parseInt(updatedItems[index].warehouse_moq || 1, 10));
    let effectiveQty = newQty;
    if (moq > 1) {
      const current = parseInt(updatedItems[index].quantity || 0, 10);
      // Detect direction from the button (+1 / -1) and step by moq.
      if (newQty === current + 1) effectiveQty = current + moq;
      else if (newQty === current - 1) effectiveQty = Math.max(moq, current - moq);
      else effectiveQty = Math.ceil(Math.max(1, newQty) / moq) * moq;
    }
    updatedItems[index].quantity = effectiveQty;
    updatedItems[index].subtotal = (updatedItems[index].discounted_price || updatedItems[index].price || updatedItems[index].mrp) * effectiveQty;
    setItems(updatedItems);
  };
  
  // Delete item
  const deleteItem = (index) => {
    if (items.length <= 1) {
      toast.error('Order must have at least one item');
      return;
    }
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
    toast.success('Item removed');
  };
  
  // Calculate totals
  const calculateTotals = () => {
    const totalMRP = items.reduce((sum, item) => sum + (item.mrp || item.price || 0) * item.quantity, 0);
    const totalAmount = items.reduce((sum, item) => {
      const price = item.discounted_price || item.price || item.mrp || 0;
      return sum + price * item.quantity;
    }, 0);
    const savings = totalMRP - totalAmount;
    
    return { totalMRP, totalAmount, savings };
  };
  
  // Check if there are changes
  const hasChanges = () => {
    if (items.length !== originalItems.length) return true;
    
    for (let i = 0; i < items.length; i++) {
      const current = items[i];
      const original = originalItems.find(o => o.jp_part_no === current.jp_part_no);
      
      if (!original) return true; // New item
      if (current.quantity !== original.quantity) return true;
    }
    
    // Check for deleted items
    for (const original of originalItems) {
      if (!items.find(i => i.jp_part_no === original.jp_part_no)) return true;
    }
    
    return false;
  };
  
  // Save changes
  const handleSave = async () => {
    if (!hasChanges()) {
      toast.info('No changes to save');
      onClose();
      return;
    }
    
    setSaving(true);
    // Hoisted so the SPLIT_SIBLING_DUPLICATE catch-branch can reuse
    // it for the override retry (2026-07-28).
    let updateData;
    try {
      const { totalMRP, totalAmount, savings } = calculateTotals();
      
      updateData = {
        items: items.map(item => ({
          jp_part_no: item.jp_part_no,
          part_name: item.part_name,
          oe_part_no: item.oe_part_no,
          mrp: item.mrp || item.price,
          price: item.discounted_price || item.price || item.mrp,
          discounted_price: item.discounted_price || item.price || item.mrp,
          quantity: item.quantity,
          subtotal: (item.discounted_price || item.price || item.mrp) * item.quantity
        })),
        total_mrp: totalMRP,
        total_amount: totalAmount,
        final_amount: totalAmount,
        savings: savings
      };
      
      // Use different endpoint for customers vs admins
      const endpoint = isCustomer 
        ? `${API}/my-orders/${order.order_id}/items`
        : `${API}/admin/orders/${order.order_id}/items`;
      
      await axios.put(endpoint, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Order updated successfully');
      if (handleOrderUpdated) handleOrderUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating order:', error);
      const detail = error.response?.data?.detail;
      // 2026-02-20 RC-151 — Warehouse-MOQ violation detail (admin path).
      if (detail && typeof detail === 'object' && detail.code === 'WAREHOUSE_MOQ_VIOLATION') {
        const lines = (detail.violations || [])
          .map(v => `${v.jp_part_no}: entered ${v.qty} → must be multiple of ${v.moq} (try ${v.suggested_qty})`)
          .join('\n');
        toast.error(`Warehouse MOQ violation:\n${lines}`, { duration: 10000 });
      } else if (detail && typeof detail === 'object' && detail.code === 'SPLIT_SIBLING_DUPLICATE') {
        // 2026-07-28 · PIONEER AUTO ORD-009443 fix. Backend blocks adding a
        // JP that already exists on a sibling split order. Show a rich
        // confirmation so the admin can either remove the sibling copy or
        // force-override with `override_sibling_duplicate=true`.
        const conflicts = detail.conflicts || [];
        const lines = conflicts
          .map(c => `• ${c.jp_part_no} (${c.part_name || '?'})\n    already on sibling ${c.sibling_order_id} (${c.sibling_source || '?'})\n    at qty=${c.sibling_qty}${c.sibling_packing_finalized_at ? ' — already packed' : ''}`)
          .join('\n');
        const proceed = window.confirm(
          `⚠️ Cross-sibling split conflict\n\n` +
          `These items are already on a sibling split order under the same customer.\n` +
          `Adding them here will cause double-picking and double-dispatch:\n\n${lines}\n\n` +
          `Recommended: remove them from the sibling order first, or reduce this qty to 0.\n\n` +
          `Click OK ONLY if the double-supply is intentional (rare — e.g. partial fulfilment across godowns).\n` +
          `Click Cancel to go back and fix.`
        );
        if (proceed) {
          // Retry once with the override flag set.
          try {
            const endpoint = isCustomer
              ? `${API}/my-orders/${order.order_id}/items`
              : `${API}/admin/orders/${order.order_id}/items`;
            const overrideData = { ...updateData, override_sibling_duplicate: true };
            await axios.put(endpoint, overrideData, {
              headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Order updated (sibling duplicate override applied)');
            if (handleOrderUpdated) handleOrderUpdated();
            onClose();
          } catch (retryErr) {
            const rd = retryErr.response?.data?.detail;
            toast.error(typeof rd === 'string' ? rd : (rd?.message || 'Override retry failed'));
          }
        } else {
          toast.info('Edit cancelled. Remove the duplicated JP(s) or reduce qty and try again.');
        }
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to update order');
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };
  
  const { totalMRP, totalAmount, savings } = calculateTotals();
  
  if (!order) return null;
  
  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-red-600" />
              Edit Order: {order.order_id}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Customer Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Customer:</span>
                  <p className="font-medium text-gray-900">{order.customer_name}</p>
                </div>
                <div>
                  <span className="text-gray-500">Company:</span>
                  <p className="font-medium text-gray-900">{order.company_name || '-'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Discount:</span>
                  <p className="font-medium text-green-600">{customerDiscount}%</p>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <Badge className="ml-1">{order.status}</Badge>
                </div>
              </div>
            </div>
            
            {/* Add New Item Section */}
            <Card className="border-dashed border-2 border-gray-300">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium text-gray-700 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add New Item
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search products by part number or name..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    className="pl-10"
                  />
                </div>
                
                {loadingProducts && (
                  <div className="text-center py-4 text-gray-500 text-sm">Searching...</div>
                )}
                
                {searchResults.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[250px] overflow-y-auto">
                    {searchResults.map((product) => {
                      const unitPrice = product.mrp || 0;
                      const hasSpecialPrice = product.has_special_price;
                      // When this customer has a special price, ALWAYS use it.
                      // Backend's `discounted_price` is computed without
                      // per-customer context (it returns the catalogue-wide
                      // discount or whichever customer's spl price the search
                      // endpoint picked), so it must NOT override the
                      // customer-specific special price we merged in above.
                      const displayPrice = hasSpecialPrice
                        ? (product.special_price || unitPrice)
                        : (product.discounted_price || (unitPrice * (1 - customerDiscount / 100)));
                      const imageUrl = productImages[product.jp_part_no];
                      
                      return (
                        <div key={product.jp_part_no} className={`border rounded-lg p-3 bg-white flex gap-3 hover:shadow-md transition-shadow ${hasSpecialPrice ? 'border-green-300 bg-green-50' : ''}`}>
                          <div className="w-16 h-16 bg-gray-50 rounded flex-shrink-0 flex items-center justify-center">
                            {imageUrl ? (
                              <img src={imageUrl} alt={product.part_name} className="max-h-full max-w-full object-contain" />
                            ) : (
                              <span className="text-xs text-gray-400">No Image</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-gray-900 text-sm line-clamp-1">{product.part_name}</h4>
                              {hasSpecialPrice && (
                                <Badge className="bg-green-100 text-green-700 text-xs px-1.5 py-0">Special</Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              <span className="font-mono text-red-600">{formatJpWithAlias(product)}</span>
                            </p>
                            <div className="mt-1"><StockBadge jpPartNo={product.jp_part_no} size="sm" /></div>
                            <div className="flex items-center justify-between mt-2">
                              <div>
                                {displayPrice < unitPrice ? (
                                  <>
                                    <span className="text-xs text-gray-400 line-through mr-1">₹{unitPrice.toFixed(2)}</span>
                                    <span className={`text-sm font-bold ${hasSpecialPrice ? 'text-amber-600' : 'text-green-600'}`}>₹{displayPrice.toFixed(2)}</span>
                                  </>
                                ) : (
                                  <span className="text-sm font-bold text-green-600">₹{displayPrice.toFixed(2)}</span>
                                )}
                              </div>
                              <Button 
                                size="sm" 
                                onClick={() => openAddItemDialog(product)}
                                className="bg-red-600 hover:bg-red-700 text-white h-7 px-2"
                              >
                                <Plus className="w-3 h-3 mr-1" /> Add
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Current Items */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Order Items ({items.length})</h3>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-[50px]">#</TableHead>
                      <TableHead>Part No</TableHead>
                      <TableHead>Part Name</TableHead>
                      <TableHead className="text-center w-[120px]">Quantity</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={`${item.jp_part_no}-${index}`} className={item._new ? 'bg-green-50' : ''}>
                        <TableCell className="font-medium">{index + 1}</TableCell>
                        <TableCell className="font-mono text-red-600 text-sm"><PartNoLabel item={item} size="sm" jpClassName="text-red-600" /></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm">{item.part_name}</span>
                            {item._new && <Badge className="bg-green-500 text-white text-xs">New</Badge>}
                            {/* 2026-02-20 RC-151 · warehouse-MOQ badge (admin path only). */}
                            {!isCustomer && parseInt(item.warehouse_moq || 1, 10) > 1 && (
                              <Badge
                                variant="outline"
                                className="text-[10px] border-amber-300 text-amber-700 bg-amber-50"
                                data-testid={`wh-moq-hint-${item.jp_part_no}`}
                              >
                                MOQ {item.warehouse_moq}
                              </Badge>
                            )}
                            <StockBadge jpPartNo={item.jp_part_no} size="sm" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => updateItemQuantity(index, item.quantity - 1)}
                              className="h-7 w-7 p-0"
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItemQuantity(index, parseInt(e.target.value) || 1)}
                              onFocus={(e) => e.target.select()}
                              className="w-14 h-7 text-center text-sm"
                            />
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => updateItemQuantity(index, item.quantity + 1)}
                              className="h-7 w-7 p-0"
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          ₹{(item.discounted_price || item.price || item.mrp || 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ₹{((item.discounted_price || item.price || item.mrp || 0) * item.quantity).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteItem(index)}
                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            disabled={items.length <= 1}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            
            {/* Order Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div className="space-y-1">
                  <p className="text-sm text-gray-500">Total MRP: <span className="line-through">₹{totalMRP.toFixed(2)}</span></p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <p className="text-2xl font-bold text-red-600">₹{totalAmount.toFixed(2)}</p>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving || !hasChanges()}
                className="bg-green-600 hover:bg-green-700 text-white px-8"
              >
                {saving ? 'Saving...' : 'Save Changes'}
                <Save className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add Item Quantity Dialog */}
      <Dialog open={!!addItemProduct} onOpenChange={() => setAddItemProduct(null)}>
        <DialogContent className="sm:max-w-md bg-white max-h-[90vh] overflow-y-auto"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            setTimeout(() => {
              if (qtyInputRef.current) {
                qtyInputRef.current.focus();
                qtyInputRef.current.select();
              }
            }, 0);
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900">Add Item to Order</DialogTitle>
          </DialogHeader>
          
          {addItemProduct && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-20 h-20 bg-gray-50 rounded flex-shrink-0 flex items-center justify-center">
                  {productImages[addItemProduct.jp_part_no] ? (
                    <img 
                      src={productImages[addItemProduct.jp_part_no]} 
                      alt={addItemProduct.part_name}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-gray-400">No Image</span>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 text-sm">{addItemProduct.part_name}</h4>
                  <p className="text-xs text-gray-500">Part No: <span className="font-mono text-red-600">{formatJpWithAlias(addItemProduct)}</span></p>
                  {addItemProduct.discounted_price && addItemProduct.discounted_price < addItemProduct.mrp ? (
                    <>
                      <p className="text-xs text-gray-400 line-through">MRP: ₹{(addItemProduct.mrp || 0).toFixed(2)}</p>
                      <p className="text-base font-bold text-green-600 mt-1">
                        ₹{(addItemProduct.discounted_price || 0).toFixed(2)}
                        {addItemProduct.has_special_price && <span className="text-xs text-amber-600 ml-1">(Special Rate)</span>}
                      </p>
                    </>
                  ) : (
                    <p className="text-base font-bold text-green-600 mt-1">
                      ₹{((addItemProduct.discounted_price || addItemProduct.mrp || 0)).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700 font-medium flex items-center gap-2">
                  Quantity
                  {(() => {
                    const _moq = parseInt(
                      addItemProduct.warehouse_moq
                      || addItemProduct.moq || 1, 10) || 1;
                    if (_moq <= 1) return null;
                    return (
                      <span
                        className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300"
                        data-testid="add-item-moq-badge">
                        MOQ: {_moq} PC
                      </span>
                    );
                  })()}
                </Label>
                <div className="flex items-center gap-3">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      const _moq = parseInt(
                        addItemProduct.warehouse_moq
                        || addItemProduct.moq || 1, 10) || 1;
                      const _step = _moq > 1 ? _moq : 1;
                      setAddItemQuantity(Math.max(0, addItemQuantity - _step));
                    }}
                    className="h-10 w-10 p-0"
                    data-testid="add-item-qty-decrement"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    ref={qtyInputRef}
                    type="number"
                    min="0"
                    value={addItemQuantity}
                    onChange={(e) => setAddItemQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                    onFocus={(e) => e.target.select()}
                    autoFocus
                    className="w-24 h-10 text-center text-lg font-bold"
                    data-testid="add-item-qty-input"
                  />
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      const _moq = parseInt(
                        addItemProduct.warehouse_moq
                        || addItemProduct.moq || 1, 10) || 1;
                      const _step = _moq > 1 ? _moq : 1;
                      // If current qty isn't a MOQ multiple, snap UP first
                      // so the very next +N click lands on a valid boundary.
                      const _q = parseInt(addItemQuantity, 10) || 0;
                      const _snapped = (_step > 1 && _q > 0 && _q % _step !== 0)
                        ? Math.ceil(_q / _step) * _step
                        : _q + _step;
                      setAddItemQuantity(_snapped);
                    }}
                    className="h-10 w-10 p-0"
                    data-testid="add-item-qty-increment"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 flex justify-between items-center">
                <span className="font-medium text-gray-700">Total:</span>
                <span className="text-xl font-bold text-green-600">
                  ₹{((addItemProduct.discounted_price || addItemProduct.mrp || 0) * addItemQuantity).toFixed(2)}
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddItemProduct(null)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmAddItem}
              disabled={addItemQuantity <= 0}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add to Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Duplicate Item Confirmation Dialog */}
      <Dialog open={!!duplicateItemConfirm} onOpenChange={() => setDuplicateItemConfirm(null)}>
        <DialogContent className="max-w-md bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Item Already in Order
            </DialogTitle>
          </DialogHeader>
          {duplicateItemConfirm && (
            <div className="py-4">
              <p className="text-gray-700 font-medium">
                {duplicateItemConfirm.product?.part_name}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Part No: <span className="font-mono text-red-600">{formatJpWithAlias(duplicateItemConfirm.product || {})}</span>
              </p>
              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-amber-800">
                  This item is already in the order with <strong>{duplicateItemConfirm.existingQty} PC</strong>.
                </p>
                <p className="text-amber-700 mt-2">
                  Do you want to add <strong>{duplicateItemConfirm.newQty} PC</strong> to the existing quantity?
                </p>
                <p className="text-amber-600 mt-2 text-sm">
                  New total: <strong>{duplicateItemConfirm.existingQty + duplicateItemConfirm.newQty} PC</strong>
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => handleDuplicateConfirm(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleDuplicateConfirm(true)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Yes, Add to Existing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditOrderModal;
